import { useState, useEffect } from 'react';
import { flightService } from '../../services/flightService';
import BeforeYouBook from '../BeforeYouBook';

// Helper function to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '/assets/images/no_aircraft.png';

  // If it's already a full URL (starts with http:// or https://), return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // If it's a relative path, prepend the API base URL (without /api/v1)
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';
  const baseUrl = apiBaseUrl.replace('/api/v1', '');
  return `${baseUrl}/${imageUrl}`;
};

const MultiCityFlightDetails = ({ selectedFlights, passengers, onNext }) => {
  const [expandedFlights, setExpandedFlights] = useState({});
  const [loading, setLoading] = useState(true);
  const [imageLoadStates, setImageLoadStates] = useState({});
  const [imageUrls, setImageUrls] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [flights, setFlights] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedFlights && selectedFlights.length > 0) {
      // Check if we already have full flight data from URL params
      const hasFullData = selectedFlights.every(f =>
        f.aircraft_images && f.departure && f.arrival
      );

      if (hasFullData) {
        // Use the data from URL params
        setFlights(selectedFlights);
        cacheImageUrls(selectedFlights);
        setLoading(false);
      } else {
        // Fetch flight details from API
        fetchFlightDetails();
      }
    }
  }, [selectedFlights]);

  const fetchFlightDetails = async () => {
    try {
      setLoading(true);

      // Extract flight IDs
      const flightIds = selectedFlights.map(f => f.id);

      console.log('🔍 Fetching multiple flight details for IDs:', flightIds);

      // Use the existing API to fetch all flights in a single call
      const response = await flightService.getMultipleFlightDetails({
        flight_ids: flightIds
      });

      console.log('✅ Multiple flight details response:', response);

      if (response.success && response.data) {
        setFlights(response.data);
        cacheImageUrls(response.data);
      } else {
        setError('Failed to load flight details');
      }
    } catch (err) {
      console.error('❌ Failed to load flight details:', err);
      setError('Failed to load flight details');
    } finally {
      setLoading(false);
    }
  };

  const cacheImageUrls = (flightData) => {
    const urls = {};
    const errors = {};

    flightData.forEach(flight => {
      if (flight.aircraft_images && flight.aircraft_images.length > 0) {
        const imageUrl = flight.aircraft_images[0].presigned_url || flight.aircraft_images[0].url;
        urls[flight.id] = getImageUrl(imageUrl); // Convert to full URL
        errors[flight.id] = false;
      } else {
        errors[flight.id] = true;
      }
    });

    setImageUrls(urls);
    setImageErrors(errors);
  };

  const toggleBeforeYouBook = (flightId) => {
    setExpandedFlights(prev => ({
      ...prev,
      [flightId]: !prev[flightId]
    }));
  };

  const calculatePricing = (flight) => {
    if (!flight) return { farePerPax: 0, totalFare: 0, blockFare: 0, isSeatPricing: false };

    let farePerPax = 0;
    let totalFare = 0;
    let blockFare = 0;
    let isSeatPricing = false;

    if (flight.block_price && flight.seat_price) {
      isSeatPricing = true;
      farePerPax = parseFloat(flight.seat_price);
      if (flight.commission_fee) {
        farePerPax = Math.round(farePerPax + (farePerPax * flight.commission_fee / 100));
      } else {
        farePerPax = Math.round(farePerPax);
      }
      totalFare = farePerPax * passengers;
    } else {
      blockFare = parseFloat(flight.block_empty_price || 0);
      if (flight.commission_fee) {
        blockFare = Math.round(blockFare + (blockFare * flight.commission_fee / 100));
      } else {
        blockFare = Math.round(blockFare);
      }
    }

    return { farePerPax, totalFare, blockFare, isSeatPricing };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDateTime = (datetime, format = 'time') => {
    const date = new Date(datetime);

    if (format === 'time') {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } else if (format === 'date') {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const year = date.getFullYear().toString().slice(-2);
      return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${year}`;
    } else if (format === 'full') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    }
  };

  const calculateDuration = (flight) => {
    if (!flight) return { hours: 0, minutes: 0 };

    // Use duration_minutes from API if available
    if (flight.duration_minutes) {
      const hours = Math.floor(flight.duration_minutes / 60);
      const minutes = flight.duration_minutes % 60;
      return { hours, minutes };
    }

    // Fallback to calculation from departure/arrival times
    const departure = new Date(flight.departure.datetime);
    const arrival = new Date(flight.arrival.datetime);
    const diffMs = arrival - departure;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return { hours, minutes };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <img src="/assets/images/flight_logo.gif" alt="Loading..." />
      </div>
    );
  }

  if (error || !flights || flights.length === 0) {
    return (
      <div className="container mx-auto px-4 mt-5">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error || 'No flights selected'}
        </div>
      </div>
    );
  }

  // Calculate grand total
  const grandTotal = flights.reduce((total, flight) => {
    const pricing = calculatePricing(flight);
    return total + (pricing.totalFare || pricing.blockFare);
  }, 0);

  return (
    <section className="py-6 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Left Column - Flight Cards and Summary */}
          <div className="lg:col-span-3 space-y-3">
            {/* Flight Cards */}
            {flights.map((flight, index) => {
              const pricing = calculatePricing(flight);
              const duration = calculateDuration(flight);
              const totalFare = pricing.totalFare || pricing.blockFare;

              return (
                <div key={flight.id || index}>
                  {/* Flight Card */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="container mx-auto px-4 pt-3 pb-3">
                      <div className="flex flex-wrap -mx-4">
                        {/* Aircraft Image */}
                        <div className="w-full xl:w-1/4 md:w-1/3 px-4">
                          <div className="w-full relative">
                            {imageUrls[flight.id] && !imageErrors[flight.id] ? (
                              <>
                                {!imageLoadStates[flight.id] && (
                                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
                                  </div>
                                )}
                                <img
                                  src={imageUrls[flight.id]}
                                  alt={flight.aircraft_images?.[0]?.name || 'Aircraft'}
                                  className={`w-full h-auto object-cover rounded transition-opacity duration-300 ${imageLoadStates[flight.id] ? 'opacity-100' : 'opacity-0'
                                    }`}
                                  onLoad={() => {
                                    setImageLoadStates(prev => ({ ...prev, [flight.id]: true }));
                                  }}
                                  onError={() => {
                                    // Mark this image as errored to prevent repeated attempts
                                    setImageErrors(prev => ({ ...prev, [flight.id]: true }));
                                    setImageLoadStates(prev => ({ ...prev, [flight.id]: true }));
                                  }}
                                />
                              </>
                            ) : (
                              <img
                                src="/assets/images/no_aircraft.png"
                                alt="Aircraft"
                                className="w-full h-auto object-cover rounded"
                              />
                            )}
                          </div>
                        </div>

                        {/* Flight Details */}
                        <div className="w-full xl:w-3/4 md:w-2/3 px-4">
                          <div className="pt-2">
                            <div className="py-2">
                              <p className="text-lg text-gray-800 font-bold m-0">
                                {flight.flight_number?.toUpperCase()}
                              </p>
                              <div className="py-2">
                                <div className="flex flex-wrap -mx-4">
                                  <div className="w-full px-4">
                                    <div className="flex flex-wrap -mx-4">
                                      <div className="w-full md:w-1/3 px-4">
                                        <h4 className="text-sm font-bold text-gray-600 mb-0">
                                          {flight.departure.airport_code} {formatDateTime(flight.departure.datetime, 'time')}
                                        </h4>
                                        <p className="mb-0">
                                          {formatDateTime(flight.departure.datetime, 'date')}
                                        </p>
                                      </div>

                                      <div className="w-full md:w-1/3 px-4 text-center">
                                        <span>→</span>
                                        <p className="mb-0">{duration.hours} Hrs {duration.minutes} Mins</p>
                                      </div>

                                      <div className="w-full md:w-1/3 px-4 text-right">
                                        <h4 className="text-sm font-bold text-gray-600 mb-0">
                                          {flight.arrival.airport_code} {formatDateTime(flight.arrival.datetime, 'time')}
                                        </h4>
                                        <p className="mb-0">
                                          {formatDateTime(flight.arrival.datetime, 'date')}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="flex flex-wrap -mx-4">
                                  <div className="w-full md:w-7/12 px-4">
                                    <div className="flex flex-col gap-1">
                                      <p className="mb-0 text-sm">Available Seats: <span className="font-bold">{flight.availableSeats}</span></p>
                                    </div>

                                    <p className="mb-0 font-bold text-black-600 text-sm">
                                      Fare/Pax ${formatCurrency(pricing.farePerPax || pricing.blockFare)}
                                      &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                                      Total Fare ${formatCurrency(totalFare)}
                                    </p>
                                  </div>
                                  <div style={{ paddingTop: '20px' }} className="w-full md:w-5/12 px-4 text-right">
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        toggleBeforeYouBook(flight.id);
                                      }}
                                      className="font-bold text-gray-600 text-sm no-underline"
                                    >
                                      Before You Book
                                      <i className={`fa fa-caret-${expandedFlights[flight.id] ? 'up' : 'down'} ml-2`}></i>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Before You Book Section */}
                  <BeforeYouBook
                    isOpen={expandedFlights[flight.id]}
                    onToggle={() => toggleBeforeYouBook(flight.id)}
                  />
                </div>
              );
            })}

            {/* Flight Summary Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-yellow-400 px-4 py-2">
                <h3 className="font-bold text-gray-800 text-sm">Flight Summary</h3>
              </div>
              <div className="overflow-x-auto px-2">
                <table className="w-full text-xs" style={{ border: '1px solid #dee2e6' }}>
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50" style={{ border: '1px solid #dee2e6' }}>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700" style={{ border: '1px solid #dee2e6' }}>Fl. No.</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700" style={{ border: '1px solid #dee2e6' }}>Dep.</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700" style={{ border: '1px solid #dee2e6' }}>ETD</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700" style={{ border: '1px solid #dee2e6' }}>Arr.</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700" style={{ border: '1px solid #dee2e6' }}>ETA</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700" style={{ border: '1px solid #dee2e6' }}>Flight Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flights.map((flight, index) => {
                      const duration = calculateDuration(flight);
                      return (
                        <tr key={flight.id || index} className={`${index < flights.length - 1 ? 'border-b border-gray-200' : ''} hover:bg-gray-50`} style={{ border: '1px solid #dee2e6' }}>
                          <td className="px-3 py-2 text-gray-800 font-medium" style={{ border: '1px solid #dee2e6' }}>{flight.flight_number?.toUpperCase()}</td>
                          <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>{flight.departure.airport_code}</td>
                          <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>
                            {formatDateTime(flight.departure.datetime, 'full')}
                          </td>
                          <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>{flight.arrival.airport_code}</td>
                          <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>
                            {formatDateTime(flight.arrival.datetime, 'full')}
                          </td>
                          <td className="px-3 py-2 text-gray-800" style={{ border: '1px solid #dee2e6' }}>
                            {duration.hours}h {duration.minutes}m
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Column - Fare Box */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm sticky top-4 overflow-hidden">
              {/* Fare Header */}
              <div className="bg-yellow-400 px-4 py-3">
                <h3 className="font-bold text-gray-800 text-lg">Fare</h3>
              </div>

              {/* Fare Content */}
              <div className="p-4 space-y-4">
                {/* Individual Flight Pricing */}
                {flights.map((flight, index) => {
                  const pricing = calculatePricing(flight);
                  const totalFare = pricing.totalFare || pricing.blockFare;

                  return (
                    <div key={flight.id || index}>
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-bold text-gray-700">{flight.departure.airport_code}</span>
                        <span className="text-gray-400">→</span>
                        <span className="text-sm font-bold text-gray-700">{flight.arrival.airport_code}</span>
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Passenger(s)</span>
                          <span className="text-gray-700 font-semibold">{passengers}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Fare/Pax</span>
                          <span className="text-gray-700 font-semibold">
                            ${formatCurrency(pricing.farePerPax || pricing.blockFare)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Total Fare</span>
                          <span className="text-gray-700 font-semibold">
                            ${formatCurrency(totalFare)}
                          </span>
                        </div>
                      </div>

                      {/* Divider between flights */}
                      {index < flights.length - 1 && (
                        <div className="border-t-2 border-dashed border-gray-300 mt-4"></div>
                      )}
                    </div>
                  );
                })}

                {/* Divider before grand total */}
                <div className="border-t-2 border-dashed border-gray-300"></div>

                {/* Grand Total */}
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-bold">Total Passenger(s)</span>
                    <span className="text-gray-700 font-bold">{passengers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-bold">Grand Total</span>
                    <span className="text-gray-700 font-bold">
                      ${formatCurrency(grandTotal)}
                    </span>
                  </div>
                </div>

                {/* Next Button */}
                <button
                  className="w-full px-4 py-3 bg-yellow-400 text-gray-800 font-bold rounded hover:bg-yellow-300 transition-colors text-sm mt-4"
                  onClick={onNext}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MultiCityFlightDetails;
